import {
    AlertCircle,
    ArrowLeft,
    CreditCard,
    Download,
    Loader,
    Printer,
    Share2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getInvoiceById } from '../../api/action/invoice';
import { getOrdersByOrderId } from '../../api/action/order';
import InvoiceDownload from './InvoiceDownload';
import { getDomainColorsByType, getDomainLabelsByType } from '../../domain/Domain';
import FormatByOperatingLocation from '../../components/FormatByOperatingLocation';
import {
    formatCurrency as formatSharedCurrency,
    formatDateInCompanyTimeZone
} from '../../utils/format';
import { getBestReceiptOrder, getOrderLineItems, getOrderPurchaseTransaction } from '../LandingPage/FlexOfficeDomain/flex-new/bookings/utils/orderUtils';
import { formatReceiptPaymentStatus, resolveReceiptStatus } from '../LandingPage/FlexOfficeDomain/flex-new/bookings/utils/receiptStatus';
import { isKioskSessionActive } from '../../domains/flexOffice/kiosk/kioskSession.js';

const PAYMENT_METHOD_LABELS = {
    ACH: 'ACH Transfer',
    BANK_TRANSFER: 'Bank Transfer',
    CARD: 'Card',
    CASH: 'Cash',
    CASH_ON_DELIVERY: 'Cash on Delivery',
    CHECK: 'Cheque',
    CHEQUE: 'Cheque',
    COD: 'Cash on Delivery',
    CREDIT_CARD: 'Credit Card',
    CRYPTO: 'Crypto',
    DEBIT_CARD: 'Debit Card',
    NET_BANKING: 'Net Banking',
    ONLINE: 'Online Payment',
    OTHER: 'Other',
    PAYPAL: 'PayPal',
    RAZORPAY: 'Razorpay',
    STRIPE: 'Stripe',
    UPI: 'UPI',
    WALLET: 'Wallet',
    WIRE: 'Wire Transfer',
    ZELLE: 'Zelle'
};

const BLANK_PAYMENT_VALUES = new Set(['', 'N/A', 'NA', 'NONE', 'NULL', 'UNDEFINED', '-', '\u2014']);

// Receipt statuses that still owe money and therefore show the Pay Now action.
const PAYABLE_RECEIPT_STATUSES = new Set(['PAYMENT_PENDING', 'CREATED']);

const normalizePaymentMethodCode = (value = '') => (
    String(value || '').trim().toUpperCase().replace(/[\s-]+/g, '_')
);

const formatPaymentMethodLabel = (value) => {
    if (!value) return 'Cash';

    const normalizedValue = normalizePaymentMethodCode(value);
    if (BLANK_PAYMENT_VALUES.has(normalizedValue)) return 'Cash';

    return PAYMENT_METHOD_LABELS[normalizedValue]
        || String(value)
            .trim()
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
};

const resolveTransactionPaymentMethodCode = (transaction = {}) => {
    const paymentMethodData = transaction?.paymentMethod;
    const paymentMethodName = normalizePaymentMethodCode(paymentMethodData?.name);

    if (paymentMethodName.includes('ZELLE')) {
        return 'ZELLE';
    }

    return normalizePaymentMethodCode(
        paymentMethodData?.paymentMethodType
        || paymentMethodData?.name
        || transaction?.paymentMethodType
        || transaction?.paymentMethod
    );
};

const resolveReceiptPaymentMethodCode = (transaction = {}, invoice = {}) => (
    resolveTransactionPaymentMethodCode(transaction)
    || normalizePaymentMethodCode(invoice?.paymentMethod)
    || normalizePaymentMethodCode(invoice?.order?.paymentMethod)
);

const buildAddressLines = (address) => {
    if (!address) return [];

    return [
        address.streetName1,
        address.streetName2,
        [address.city, address.state, address.zipcode].filter(Boolean).join(', '),
        address.country
    ].filter(Boolean);
};

const getPrimaryReservationDate = (lineItems = []) => {
    const reservationDates = lineItems
        .map((item) =>
            item?.scheduledProduct?.period?.effectiveDate?.localStartDate
            || item?.scheduledProduct?.period?.effectiveStartDate
            || ''
        )
        .filter(Boolean)
        .sort((leftDate, rightDate) => new Date(leftDate) - new Date(rightDate));

    return reservationDates[0] || '';
};

const printReceiptNode = (sourceNode, title) => {
    if (!sourceNode) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const width = sourceNode.scrollWidth || 794;
    const inheritedStyles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
        .map((node) => node.outerHTML)
        .join('\n');

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
            <head>
                <title>${String(title || 'Receipt').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</title>
                ${inheritedStyles}
                <style>
                    * { box-sizing: border-box; }
                    html, body { margin: 0; padding: 0; background: #fff; }
                    body {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .print-shell {
                        width: ${width}px;
                        margin: 0 auto;
                        background: #fff;
                    }
                    @page { margin: 0; size: A4 portrait; }
                </style>
            </head>
            <body>
                <div class="print-shell">${sourceNode.outerHTML}</div>
                <script>
                    window.onload = function () {
                        setTimeout(function () { window.print(); }, 150);
                    };
                    window.onafterprint = function () { window.close(); };
                </script>
            </body>
        </html>
    `);
    printWindow.document.close();
};

const getReceiptTheme = (domainType, domainColors) => {
    if (domainType === 'FLEX_OFFICE') {
        return {
            accent: domainColors.primary || 'var(--flex-primary, #2563EB)',
            border: 'var(--flex-primary-muted, #BFDBFE)',
            highlight: 'var(--flex-primary-soft, #DBEAFE)',
            soft: 'var(--flex-primary-soft, #EFF6FF)',
            softAlt: 'var(--flex-surface, #F8FBFF)',
            text: 'var(--flex-primary, #1D4ED8)'
        };
    }

    if (domainType === 'RETAIL') {
        return {
            accent: domainColors.primary || '#EAAA3A',
            border: '#F5D39B',
            highlight: '#FDEBC8',
            soft: '#FFF7E8',
            softAlt: '#FFFDF8',
            text: '#B45309'
        };
    }

    return {
        accent: domainColors.primary || 'var(--education-primary, #746EAC)',
        border: 'var(--education-primary-muted, #DDD8F2)',
        highlight: 'var(--education-primary-soft, #E9E4FA)',
        soft: 'var(--education-primary-soft, #F6F4FF)',
        softAlt: 'var(--education-surface, #FBFAFF)',
        text: 'var(--education-primary, #746EAC)'
    };
};

const hasReceiptUserDetails = (user) => Boolean(
    user?.firstName
    || user?.lastName
    || user?.email
    || user?.phoneNumber
    || user?.addressList?.length
);

const resolveReceiptUser = (...users) => (
    users.find(hasReceiptUserDetails) || users.find(Boolean) || {}
);

function ViewInvoice() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state) => state?.auth?.isAuthenticated);
    const authUser = useSelector((state) => state?.auth?.data) || {};
    const navigationOrder = location.state?.order || null;
    const navigationInvoice = location.state?.invoice || null;

    const [invoice, setInvoice] = useState(navigationInvoice);
    const [order, setOrder] = useState(() => getBestReceiptOrder(navigationOrder, navigationInvoice?.order));
    const [loading, setLoading] = useState(true);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [actionNotice, setActionNotice] = useState(null);
    const [downloadState, setDownloadState] = useState({
        isOpen: false,
        isLoading: false,
        error: null
    });
    const receiptUser = resolveReceiptUser(invoice?.user, order?.user, invoice?.order?.user, authUser);
    const receiptContainerRef = useRef(null);
    // Kiosk guests view invoices on a shared device — no printing, downloading,
    // or sharing out of the device, only the read-only receipt details below.
    const isKiosk = isKioskSessionActive();

    useEffect(() => {
        if (id) {
            localStorage.setItem("invoiceprimaryId", id);
        }
    }, [id]);

    const handleDownload = async () => {
        setDownloadState({
            isOpen: true,
            isLoading: true,
            error: null
        });

        try {
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Download timed out after 30 seconds')), 30000)
            );

            await Promise.race([
                new Promise(resolve => setTimeout(resolve, 1000)),
                timeoutPromise
            ]);

            setDownloadState({
                isOpen: true,
                isLoading: false,
                error: null
            });
        } catch (error) {
            setDownloadState({
                isOpen: true,
                isLoading: false,
                error: error.message
            });
        }
    };

    const handleCloseDownload = () => {
        setDownloadState({
            isOpen: false,
            isLoading: false,
            error: null
        });
    };

    useEffect(() => {
        const fetchInvoiceAndOrderDetails = async () => {
            if (!id) return;
            setLoading(true);
            setError(null);
            try {
                const invoiceData = await dispatch(getInvoiceById(id));
                let fetchedOrder = null;
                if (invoiceData?.order?.primaryId) {
                    const orderData = await dispatch(getOrdersByOrderId(invoiceData.order.primaryId));
                    fetchedOrder = Array.isArray(orderData) ? orderData[0] : orderData;
                }

                const resolvedOrder = getBestReceiptOrder(fetchedOrder, navigationOrder, invoiceData?.order);
                setInvoice({
                    ...invoiceData,
                    order: resolvedOrder || invoiceData?.order
                });
                setOrder(resolvedOrder);
            } catch (error) {
                setError(error.message || 'Failed to load invoice details');
            } finally {
                setLoading(false);
            }
        };
        fetchInvoiceAndOrderDetails();
    }, [dispatch, id, navigationOrder]);

    const formatContext = invoice?.company || order?.company || invoice?.order?.company || authUser?.account?.company || localStorage.getItem('domainType') || 'US';

    const formatDate = (dateString) => (
        formatDateInCompanyTimeZone(dateString, formatContext, 'MMM d, yyyy', '')
    );

    const formatDateTime = (dateString) => (
        formatDateInCompanyTimeZone(dateString, formatContext, 'MMMM d, yyyy, h:mm a', '')
    );

    // localStartDate/localEndDate are already wall-clock values in the company's
    // timezone (no UTC offset), so they must be displayed as-is rather than run
    // through formatDateInCompanyTimeZone, which would re-apply the company's
    // offset on top of the browser's local parsing and shift the displayed time.
    const formatWallClockDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return Number.isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatWallClockTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return Number.isNaN(date.getTime())
            ? ''
            : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const formatBookingDuration = (scheduledProduct) => {
        if (!scheduledProduct?.period) return null;
        const period = scheduledProduct.period;
        if (!period.effectiveDate?.localStartDate || !period.effectiveDate?.localEndDate) {
            return null;
        }
        try {
            const dateStr = formatWallClockDate(period.effectiveDate.localStartDate);
            const startTime = formatWallClockTime(period.effectiveDate.localStartDate);
            const endTime = formatWallClockTime(period.effectiveDate.localEndDate);
            const hasMeaningfulTime = !(startTime === '12:00 AM' && endTime === '12:00 AM');
            let durationStr = '';
            if (period.duration && period.durationType) {
                const formattedType = period.durationType.charAt(0).toUpperCase() + period.durationType.slice(1).toLowerCase();
                durationStr = `${period.duration} ${formattedType}s`;
            }
            return {
                dateStr,
                startTime,
                endTime,
                hasMeaningfulTime,
                durationStr,
                fullString: hasMeaningfulTime
                    ? `${dateStr}, ${startTime} - ${endTime}${durationStr ? ` (${durationStr})` : ''}`
                    : dateStr,
                timeZone: period.effectiveDate.timeZone || 'Local Time'
            };
        } catch {
            return null;
        }
    };

    const getScheduledProduct = (lineItem) => {
        return lineItem?.scheduledProduct;
    };

    const getDocumentType = () => {
        const domainType = invoice?.company?.domain?.type;
        const labels = getDomainLabelsByType(domainType);
        if (domainType === 'RETAIL') {
            return 'Order';
        } else if (domainType === 'FLEX_OFFICE' || domainType === 'EDUCATION') {
            return labels.receipt;
        }
        return invoice?.type === 'RECEIPT' ? labels.receipt : 'Invoice';
    };

    const getDomainSpecificColors = (domainType) => {
        const domainColors = getDomainColorsByType(domainType);
        if (domainType === 'FLEX_OFFICE') {
            return {
                primary: 'var(--flex-primary, #2563eb)',
                secondary: 'var(--flex-secondary, #0f172a)',
                accent: 'var(--flex-accent, #2563eb)',
                background: 'var(--flex-bg, #f8f9fa)',
                paper: 'var(--flex-surface, #ffffff)',
                textDark: 'var(--flex-text, #2d3436)',
                textLight: 'var(--flex-text-muted, #636e72)',
                border: 'var(--flex-border, #e0e0e0)',
                success: '#00b894',
                warning: '#fdcb6e',
                error: '#d63031',
                info: 'var(--flex-primary, #2563eb)'
            };
        }
        if (domainType === 'EDUCATION') {
            return {
                primary: 'var(--education-primary, #746EAC)',
                secondary: 'var(--education-secondary, #3B9394)',
                accent: '#B5A262',
                background: 'var(--education-background, #f8f9fa)',
                paper: 'var(--education-surface, #ffffff)',
                textDark: 'var(--education-text, #2d3436)',
                textLight: 'var(--education-muted, #636e72)',
                border: 'var(--education-border, #e0e0e0)',
                success: '#00b894',
                warning: '#fdcb6e',
                error: '#d63031',
                info: 'var(--education-secondary, #3B9394)'
            };
        }
        if (domainType === 'RETAIL') {
            return {
                primary: '#eaaa3a',
                secondary: '#10B981',
                accent: '#8B5CF6',
                background: '#f8f9fa',
                paper: '#ffffff',
                textDark: '#2d3436',
                textLight: '#636e72',
                border: '#e0e0e0',
                success: '#00b894',
                warning: '#fdcb6e',
                error: '#d63031',
                info: '#3B82F6'
            };
        }
        return domainColors;
    };

    const getBorderColor = (domainType, primaryColor) => {
        if (domainType === 'FLEX_OFFICE') return 'var(--flex-primary-muted, rgba(37, 99, 235, 0.3))';
        if (domainType === 'EDUCATION') return 'var(--education-primary-muted, rgba(116, 110, 172, 0.2))';
        return `color-mix(in srgb, ${primaryColor} 24%, transparent)`;
    };

    const getSoftBackgroundColor = (domainType, primaryColor) => {
        if (domainType === 'FLEX_OFFICE') {
            return 'var(--flex-primary-soft, rgba(37, 99, 235, 0.16))';
        }
        if (domainType === 'EDUCATION') {
            return 'var(--education-primary-soft, rgba(116, 110, 172, 0.1))';
        }
        return `color-mix(in srgb, ${primaryColor} 12%, transparent)`;
    };

    const getShippingInfo = () => {
        if (!order?.shippingAddress) return null;
        const address = order.shippingAddress;
        const estimatedDelivery = order?.estimatedDeliveryDate;
        return {
            address: address,
            estimatedDelivery: estimatedDelivery,
            shippingMethod: order?.shippingMethod || 'Standard Shipping'
        };
    };

    const documentType = getDocumentType();
    const primaryTransaction = getOrderPurchaseTransaction(order) || getOrderPurchaseTransaction(invoice?.order) || {};
    const paymentMethod = resolveReceiptPaymentMethodCode(primaryTransaction, invoice);

    const handlePayNow = async () => {
        setPaymentProcessing(true);
        setActionNotice(null);
        try {
            const phoneNumber = receiptUser?.phoneNumber || '';
            if (!phoneNumber) {
                throw new Error('Customer phone number not found');
            }
            const paymentState = {
                phoneNumber,
                invoiceId: id,
                amount: order?.totalAmount || invoice.order?.totalAmount || 0,
                order: order || invoice?.order,
                user: receiptUser,
                invoice: invoice || '',
                originalPaymentMethod: paymentMethod,
            };
            const shouldSkipOtp = invoice?.company?.domain?.type === 'KRUMAGO'
                || Boolean(isAuthenticated || authUser?.primaryId || authUser?.email);

            navigate(shouldSkipOtp ? '/invoice-payment' : '/confirm-login', {
                state: paymentState
            });
        } catch (error) {
            setActionNotice({
                type: 'error',
                message: error.message || 'Payment failed to initiate. Please try again.'
            });
        } finally {
            setPaymentProcessing(false);
        }
    };

    const handlePrint = () => {
        if (!receiptContainerRef.current) return;
        printReceiptNode(receiptContainerRef.current, `${documentType} ${invoice?.number || invoice?.id || ''}`);
    };

    const handleShare = async () => {
        const shareTitle = `${documentType} ${invoice.number || invoice.id}`;
        const shareText = `${shareTitle} - ${formatSharedCurrency(invoice.total || invoice.orderTotal || order?.totalAmount || 0, formatContext)}`;

        setActionNotice(null);

        try {
            if (navigator.share) {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: window.location.href
                });
                return;
            }

            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(window.location.href);
                setActionNotice({
                    type: 'success',
                    message: `${documentType} link copied to clipboard.`
                });
                return;
            }

            setActionNotice({
                type: 'warning',
                message: 'Sharing is unavailable in this browser.'
            });
        } catch (shareError) {
            if (shareError?.name !== 'AbortError') {
                setActionNotice({
                    type: 'error',
                    message: 'Unable to share this document right now.'
                });
            }
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const fallbackDomainType = invoice?.company?.domain?.type || localStorage.getItem('domainType') || 'EDUCATION';
    const fallbackColors = getDomainSpecificColors(fallbackDomainType);

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Loader className="animate-spin h-12 w-12 mx-auto mb-4" style={{ color: fallbackColors.primary }} />
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading {documentType}</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 text-white rounded-lg transition-all shadow-sm"
                        style={{ backgroundColor: fallbackColors.primary }}
                    >
                        Back to Documents
                    </button>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">{documentType} not found</p>
                    <button
                        onClick={handleBack}
                        className="px-4 py-2 text-white rounded-lg transition-all shadow-sm"
                        style={{ backgroundColor: fallbackColors.primary }}
                    >
                        Back to Documents
                    </button>
                </div>
            </div>
        );
    }

    const domainType = invoice?.company?.domain?.type;
    const domainColors = getDomainSpecificColors(domainType);
    const domainLabels = getDomainLabelsByType(domainType);
    const displayOrder = getBestReceiptOrder(order, invoice?.order);

    const statusConfig = resolveReceiptStatus(invoice, displayOrder);
    const totalAmount = displayOrder?.totalAmount || invoice.order?.totalAmount || 0;
    const subtotal = displayOrder?.totalBeforTax || displayOrder?.subtotalAmount || totalAmount;

    // const totalAmtAfterDiscount = (order?.totalAmtAfterDiscount === 0 || !order?.totalAmtAfterDiscount)
    //     ? order?.totalAmount
    const totalAmtAfterDiscount = displayOrder?.totalAmtAfterDiscount !== undefined && displayOrder?.totalAmtAfterDiscount !== null
        ? displayOrder.totalAmtAfterDiscount
        : Math.max(0, subtotal + (displayOrder?.tax || 0) + (displayOrder?.shippingAndHandlingAmount || 0) - (displayOrder?.totalDiscountAmt || 0));

    const lineItems = getOrderLineItems(displayOrder).length > 0 ? getOrderLineItems(displayOrder) : (invoice.items || []);
    const isPaid = statusConfig.value === 'PAID';
    const isCancelled = statusConfig.value === 'CANCELLED';
    const isRefunded = statusConfig.value === 'REFUNDED';
    const isPartiallyRefunded = statusConfig.value === 'PARTIALLY_REFUNDED';
    // A CREATED receipt is an issued but unpaid bill - subscription fee bills
    // land here - so it is payable just like a PAYMENT_PENDING one.
    const showPayNow = !isPaid && !isCancelled && !isRefunded && !isPartiallyRefunded &&
        PAYABLE_RECEIPT_STATUSES.has(statusConfig.value);
    const isPaymentRequired = showPayNow;

    const shippingInfo = getShippingInfo();
    const isRetailDomain = domainType === 'RETAIL';
    const isEducationDomain = domainType === 'EDUCATION';
    const isFlexOfficeDomain = domainType === 'FLEX_OFFICE';

    const primaryColor = domainColors.primary;
    const secondaryColor = domainColors.secondary;

    const borderColor = getBorderColor(domainType, primaryColor);

    // Helper text for domain-specific labels
    const getDateLabel = () => {
        if (isRetailDomain) return 'Order Date';
        if (isEducationDomain) return 'Enrollment Date';
        return 'Booking Date';
    };

    const getThankYouMessage = () => {
        if (isRetailDomain) return 'Thank you for your purchase!';
        if (isEducationDomain) return 'Thank you for your enrollment!';
        return 'Thank you for your business!';
    };

    const getHomeButtonText = () => {
        if (isRetailDomain) return 'Continue Shopping';
        if (isEducationDomain) return 'Browse Courses';
        return invoice.company?.name || 'Back to Home';
    };

    const accentColor = domainColors.accent || secondaryColor;
    const receiptTheme = getReceiptTheme(domainType, domainColors);
    const companyAddressLines = buildAddressLines(invoice.company?.address);
    const customerAddressLines = buildAddressLines(receiptUser.addressList?.[0]);
    const customerName = receiptUser.firstName || receiptUser.lastName
        ? `${receiptUser.firstName || ''} ${receiptUser.lastName || ''}`.trim()
        : receiptUser.email;
    const paymentStatus = invoice.paymentStatus || primaryTransaction?.currentStatus || '';
    const paymentStatusLabel = formatReceiptPaymentStatus(paymentStatus, invoice, displayOrder);
    const paymentReference = invoice.transactionId
        || primaryTransaction?.transactionId
        || primaryTransaction?.referenceId
        || '';
    const reservationDateValue = getPrimaryReservationDate(lineItems);
    // reservationDateValue is a wall-clock localStartDate (no UTC offset) when
    // available — format it as-is rather than through the company-timezone-aware
    // formatDate, which would shift it the same way formatBookingDuration did.
    const reservationDateDisplay = reservationDateValue
        ? formatWallClockDate(reservationDateValue)
        : formatDate(displayOrder?.createdDate || order?.createdDate);

    return (
        <div className="min-h-screen bg-white" style={{ paddingTop: '120px' }}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleBack}
                                className="p-2 text-gray-500 rounded-lg transition-all"
                                onMouseOver={(event) => {
                                    event.currentTarget.style.color = primaryColor;
                                    event.currentTarget.style.backgroundColor = getSoftBackgroundColor(domainType, primaryColor);
                                }}
                                onMouseOut={(event) => {
                                    event.currentTarget.style.color = '';
                                    event.currentTarget.style.backgroundColor = '';
                                }}
                                title="Back to invoices"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {documentType} #{invoice.number || invoice.id}
                                </h1>
                                <div className="mt-1 flex flex-col items-start gap-1.5">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.badgeClassName}`}>
                                        {statusConfig.label}
                                    </span>
                                    {statusConfig.helperText ? (
                                        <p className="text-xs leading-5 text-slate-500">
                                            {statusConfig.helperText}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {showPayNow && (
                                <>
                                    {/* Pay Now button: shown whenever the receipt still owes payment.
                                        Education students pay course fee bills here too, verifying
                                        with an OTP through /confirm-login when not signed in. */}
                                    {isPaymentRequired && (
                                        <button
                                            onClick={handlePayNow}
                                            disabled={paymentProcessing}
                                            className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg flex items-center gap-2 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                        >
                                            <CreditCard size={16} />
                                            {paymentProcessing ? 'Processing...' : 'Pay'}
                                        </button>
                                    )}
                                </>
                            )}

                            {!isKiosk && (
                                <>
                                    <button
                                        onClick={handlePrint}
                                        className="rounded-lg border px-4 py-2.5 text-sm font-medium transition-all"
                                        style={{
                                            backgroundColor: '#FFFFFF',
                                            borderColor: receiptTheme.border,
                                            color: '#374151'
                                        }}
                                    >
                                        <span className="flex items-center gap-2">
                                            <Printer size={16} />
                                            Print
                                        </span>
                                    </button>

                                    <button
                                        onClick={handleDownload}
                                        disabled={downloadState.isLoading}
                                        className="rounded-lg border px-4 py-2.5 text-sm font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50"
                                        style={{
                                            backgroundColor: '#FFFFFF',
                                            borderColor: receiptTheme.accent,
                                            color: receiptTheme.text
                                        }}
                                    >
                                        {downloadState.isLoading ? (
                                            <>
                                                <Loader size={16} className="animate-spin" />
                                                Preparing Download...
                                            </>
                                        ) : (
                                            <>
                                                <Download size={16} />
                                                Download
                                            </>
                                        )}
                                    </button>

                                    <button
                                        onClick={handleShare}
                                        className="rounded-lg border px-4 py-2.5 text-sm font-medium transition-all"
                                        style={{
                                            backgroundColor: receiptTheme.soft,
                                            borderColor: receiptTheme.border,
                                            color: receiptTheme.text
                                        }}
                                    >
                                        <span className="flex items-center gap-2">
                                            <Share2 size={16} />
                                            Share
                                        </span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {isKiosk && (
                        <div className="mb-6 rounded-lg border px-4 py-3 text-sm font-medium border-slate-200 bg-slate-50 text-slate-600">
                            Printing and downloading aren't available on this kiosk. Please see staff if you need a copy of this receipt.
                        </div>
                    )}

                    {actionNotice && (
                        <div
                            className={`mb-6 rounded-lg border px-4 py-3 text-sm font-medium ${
                                actionNotice.type === 'success'
                                    ? 'border-green-200 bg-green-50 text-green-800'
                                    : actionNotice.type === 'warning'
                                        ? 'border-yellow-200 bg-yellow-50 text-yellow-800'
                                        : 'border-red-200 bg-red-50 text-red-800'
                            }`}
                            role="status"
                        >
                            {actionNotice.message}
                        </div>
                    )}
                </div>

                <div
                    ref={receiptContainerRef}
                    className="receipt-container mx-auto mb-8 w-full max-w-[794px] overflow-hidden rounded-2xl border bg-white shadow-lg"
                    style={{ borderColor: receiptTheme.border }}
                >
                    <div
                        className="receipt-accent-bar h-1.5"
                        style={{ background: `linear-gradient(90deg, #111827 0%, ${primaryColor}55%, ${accentColor} 100%)` }}
                    />

                    <div className="px-6 pb-5 pt-10 sm:px-10 sm:pt-12">
                        <div className="mb-9 flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                            <div className="max-w-[240px]">
                                <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-gray-500">From</p>
                                {invoice.company?.logoLocation ? (
                                    <img
                                        src={invoice.company.logoLocation}
                                        alt={invoice.company.name}
                                        className="mb-3 h-11 w-auto object-contain"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                ) : null}

                                <div className="mb-2 text-lg font-extrabold tracking-[-0.02em] text-gray-900">
                                    {invoice.company?.name || 'Your Company'}
                                </div>

                                {companyAddressLines.map((line) => (
                                    <p key={line} className="text-[11px] leading-[1.8] text-gray-500">
                                        {line}
                                    </p>
                                ))}
                                {invoice.company?.contactPerson?.email && (
                                    <p className="text-[11px] leading-[1.8] text-gray-500">
                                        {invoice.company.contactPerson.email}
                                    </p>
                                )}
                                {invoice.company?.contactPerson?.phoneNumber && (
                                    <div className="text-[11px] leading-[1.8] text-gray-500">
                                        <FormatByOperatingLocation
                                            value={invoice.company.contactPerson.phoneNumber}
                                            type="PHONE"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="text-left sm:text-right">
                                <h2 className="mb-3 text-[2rem] font-black uppercase leading-none tracking-[-0.06em] text-gray-900 sm:text-[2.125rem]">
                                    {documentType}
                                </h2>
                                <div className="space-y-1 text-sm leading-8 text-gray-600">
                                    <div>
                                        <span className="text-gray-400">No. </span>
                                        <span className="font-mono text-xs font-bold text-gray-900">
                                            {invoice.number || invoice.id}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400">Status </span>
                                        <span className="font-semibold text-gray-700">{statusConfig.label}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mb-7 h-px bg-gray-200" />

                        <div className="mb-9 grid grid-cols-1 gap-6 md:grid-cols-[1.6fr_1fr_1fr] md:gap-0">
                            <div className="md:pr-7">
                                <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-gray-500">Bill To</p>
                                <p className="mb-1 text-sm font-bold text-gray-900">{customerName || '—'}</p>
                                {receiptUser.email && (
                                    <p className="text-[11px] text-gray-500">{receiptUser.email}</p>
                                )}
                                {receiptUser.phoneNumber && (
                                    <div className="text-[11px] text-gray-500">
                                        <FormatByOperatingLocation value={receiptUser.phoneNumber} type="PHONE" />
                                    </div>
                                )}
                                {customerAddressLines.map((line) => (
                                    <p key={line} className="text-[11px] text-gray-500">
                                        {line}
                                    </p>
                                ))}
                            </div>

                            <div className="md:border-l md:border-gray-200 md:px-7">
                                <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-gray-500">Payment</p>
                                <p className="text-[13px] font-semibold text-gray-700">
                                    {formatPaymentMethodLabel(paymentMethod)}
                                </p>
                                {paymentStatusLabel && (
                                    <p className="mt-1.5 text-[11px] text-gray-500">Status: {paymentStatusLabel}</p>
                                )}
                                {paymentReference && (
                                    <p className="mt-1 text-[10px] leading-5 text-gray-500 break-all">
                                        Ref ID: {paymentReference}
                                    </p>
                                )}
                            </div>

                            <div className="md:border-l md:border-gray-200 md:pl-7">
                                <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-gray-500">{getDateLabel()}</p>
                                <p className="text-[13px] font-semibold text-gray-700">{reservationDateDisplay || '—'}</p>
                            </div>
                        </div>

                        {isRetailDomain && shippingInfo && (
                            <div className="mb-8 rounded-lg border px-5 py-4" style={{ backgroundColor: receiptTheme.softAlt, borderColor: receiptTheme.border }}>
                                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                                    <div>
                                        <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-gray-500">Shipping Address</p>
                                        <div className="space-y-1 text-[11px] text-gray-600">
                                            {buildAddressLines(shippingInfo.address).map((line) => (
                                                <p key={line}>{line}</p>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-gray-500">Delivery Details</p>
                                        <div className="space-y-1 text-[11px] text-gray-600">
                                            <p>Method: <span className="font-semibold text-gray-700">{shippingInfo.shippingMethod}</span></p>
                                            {shippingInfo.estimatedDelivery && (
                                                <p>Estimated: <span className="font-semibold text-gray-700">{formatDate(shippingInfo.estimatedDelivery)}</span></p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mb-8 overflow-hidden rounded-lg border" style={{ borderColor: receiptTheme.border }}>
                            <div className="grid grid-cols-[minmax(0,2.4fr)_68px_108px_108px] px-5 py-3" style={{ backgroundColor: receiptTheme.softAlt }}>
                                {[
                                    { align: 'text-left', label: 'Description' },
                                    { align: 'text-right', label: 'Qty' },
                                    { align: 'text-right', label: isRetailDomain ? 'Unit Price' : 'Rate' },
                                    { align: 'text-right', label: 'Amount' }
                                ].map(({ align, label }) => (
                                    <div key={label} className={`text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500 ${align}`}>
                                        {label}
                                    </div>
                                ))}
                            </div>

                            {lineItems.length > 0 ? (
                                lineItems.map((item, index) => {
                                    const product = item.scheduledProduct?.product || item.product;
                                    const scheduledProduct = getScheduledProduct(item);
                                    const bookingDuration = formatBookingDuration(scheduledProduct);
                                    const price = product?.price?.priceAmt || 0;
                                    const quantity = item.quantity || 1;
                                    const groupedSessionCount = Number(item?.groupedSessionCount) || 0;
                                    const sessionDuration = Number(scheduledProduct?.period?.duration) || 0;
                                    const itemAmount = item.totalAmount || (isRetailDomain ? price * quantity : price * quantity) || subtotal;

                                    return (
                                        <div
                                            key={index}
                                            className="grid grid-cols-[minmax(0,2.4fr)_68px_108px_108px] items-start gap-0 border-t px-5 py-3.5"
                                            style={{
                                                backgroundColor: index % 2 === 0 ? '#FFFFFF' : receiptTheme.softAlt,
                                                borderColor: receiptTheme.border
                                            }}
                                        >
                                            <div className="pr-4">
                                                <p className="text-[13px] font-semibold text-gray-950">
                                                    {product?.name || scheduledProduct?.name || 'Unnamed Item'}
                                                </p>
                                                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
                                                    <span>{product?.category?.name || (isRetailDomain ? 'Product' : domainLabels.space)}</span>
                                                    {isRetailDomain && product?.sku && <span>SKU: {product.sku}</span>}
                                                </div>
                                                {!isEducationDomain && !isRetailDomain && (
                                                    <div className="mt-2 space-y-1 text-[11px] text-gray-500">
                                                        {groupedSessionCount > 1 && (
                                                            <p>{groupedSessionCount} sessions{sessionDuration > 0 ? ` • ${sessionDuration} hour${sessionDuration === 1 ? '' : 's'} each` : ''}</p>
                                                        )}
                                                        {bookingDuration ? (
                                                            <>
                                                                <p>{bookingDuration.dateStr}</p>
                                                                {bookingDuration.hasMeaningfulTime && (
                                                                    <p>{bookingDuration.startTime} - {bookingDuration.endTime}</p>
                                                                )}
                                                                {isFlexOfficeDomain && bookingDuration.durationStr && bookingDuration.hasMeaningfulTime && (
                                                                    <p>{bookingDuration.durationStr}</p>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <p>No schedule specified</p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-right text-xs text-gray-500">
                                                {groupedSessionCount > 1 ? groupedSessionCount : quantity}
                                            </div>
                                            <div className="text-right text-xs text-gray-500">
                                                <FormatByOperatingLocation value={price} type="CURRENCY" />
                                            </div>
                                            <div className="text-right text-[13px] font-bold text-gray-900">
                                                <FormatByOperatingLocation value={itemAmount} type="CURRENCY" />
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="px-5 py-8 text-center text-sm italic text-gray-400">
                                    No items in this {documentType.toLowerCase()}
                                </div>
                            )}
                        </div>

                        <div className="mb-8 flex justify-end">
                            <div className="w-full max-w-[260px]">
                                <div className="flex justify-between border-b py-2 text-sm" style={{ borderColor: receiptTheme.border }}>
                                    <span className="text-gray-500">Subtotal</span>
                                    <span className="font-semibold text-gray-700">
                                        <FormatByOperatingLocation value={subtotal} type="CURRENCY" />
                                    </span>
                                </div>

                                <div className="flex justify-between border-b py-2 text-sm" style={{ borderColor: receiptTheme.border }}>
                                    <span className="text-gray-500">Taxes</span>
                                    <span className="font-semibold text-gray-700">
                                        <FormatByOperatingLocation value={order?.tax || 0} type="CURRENCY" />
                                    </span>
                                </div>

                                {order?.shippingAndHandlingAmount > 0 && (
                                    <div className="flex justify-between border-b py-2 text-sm" style={{ borderColor: receiptTheme.border }}>
                                        <span className="text-gray-500">Shipping</span>
                                        <span className="font-semibold text-gray-700">
                                            <FormatByOperatingLocation value={order.shippingAndHandlingAmount} type="CURRENCY" />
                                        </span>
                                    </div>
                                )}

                                {order?.totalDiscountAmt > 0 && (
                                    <div className="flex justify-between border-b py-2 text-sm" style={{ borderColor: receiptTheme.border }}>
                                        <span className="text-amber-700">Discount</span>
                                        <span className="font-semibold text-red-600">
                                            -<FormatByOperatingLocation value={order.totalDiscountAmt} type="CURRENCY" />
                                        </span>
                                    </div>
                                )}

                                <div className="flex items-baseline justify-between pt-3">
                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-700">
                                        {isPaid ? 'Total Paid' : 'Total Due'}
                                    </span>
                                    <span className="text-[22px] font-black tracking-[-0.03em] text-gray-900">
                                        <FormatByOperatingLocation value={totalAmtAfterDiscount} type="CURRENCY" />
                                    </span>
                                </div>
                                {isPaid && (
                                    <p className="mt-1 text-right text-[10px] text-gray-400">
                                        Paid on {formatDateTime(invoice.createdDate)}
                                    </p>
                                )}
                            </div>
                        </div>

                        {(invoice.memo || invoice.notes || order?.notes) && (
                            <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
                                {invoice.memo && (
                                    <div className="rounded-lg border px-4 py-3.5" style={{ backgroundColor: receiptTheme.softAlt, borderColor: receiptTheme.border }}>
                                        <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-gray-500">Memo</p>
                                        <p className="text-sm leading-6 text-gray-600">{invoice.memo}</p>
                                    </div>
                                )}
                                {(invoice.notes || order?.notes) && (
                                    <div className="rounded-lg border px-4 py-3.5" style={{ backgroundColor: receiptTheme.softAlt, borderColor: receiptTheme.border }}>
                                        <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-gray-500">Notes</p>
                                        <p className="text-sm leading-6 text-gray-600">{invoice.notes || order?.notes}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="border-t border-gray-200 pt-5">
                            <div className="flex flex-col gap-3 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
                                <p className="text-[9px] font-bold uppercase tracking-[0.28em] text-gray-300">
                                    {getThankYouMessage()}
                                </p>
                                <p className="font-mono text-[9px] tracking-[0.08em] text-gray-300">
                                    {invoice.number || invoice.id}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div
                        className="receipt-accent-bar h-1"
                        style={{ background: `linear-gradient(90deg, ${accentColor} 0%, ${primaryColor} 55%, #111827 100%)` }}
                    />
                </div>

                <div className="pb-6 text-center">
                    <button
                        onClick={() => navigate("/")}
                        className="rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
                        style={{
                            color: primaryColor,
                            backgroundColor: getSoftBackgroundColor(domainType, primaryColor)
                        }}
                        type="button"
                        onMouseOver={(e) => e.target.style.backgroundColor = getBorderColor(domainType, primaryColor)}
                        onMouseOut={(e) => e.target.style.backgroundColor = getSoftBackgroundColor(domainType, primaryColor)}
                    >
                        {getHomeButtonText()}
                    </button>
                </div>

                <style>{`
                    @media print {
                        body {
                            background: white !important;
                            padding: 0 !important;
                            margin: 0 !important;
                        }
                        .receipt-container {
                            box-shadow: none !important;
                            border: none !important;
                            border-radius: 0 !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            max-width: 100% !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .receipt-accent-bar {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .bg-gradient-to-r {
                            background: ${domainType === 'FLEX_OFFICE' || domainType === 'EDUCATION' || domainType === 'RETAIL' ? primaryColor : `linear-gradient(to right, ${primaryColor}, ${secondaryColor})`} !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .bg-gradient-to-br {
                            background: ${domainType === 'FLEX_OFFICE' || domainType === 'EDUCATION' || domainType === 'RETAIL' ? getSoftBackgroundColor(domainType, primaryColor) : `linear-gradient(to bottom right, color-mix(in srgb, ${primaryColor} 12%, transparent), color-mix(in srgb, ${secondaryColor} 12%, transparent))`} !important;
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                        }
                        .border-blue-100 {
                            border-color: ${borderColor} !important;
                        }
                        .text-blue-600 {
                            color: ${primaryColor} !important;
                        }
                        .text-green-600 {
                            color: #059669 !important;
                        }
                        .text-gray-500 {
                            color: #6b7280 !important;
                        }
                        .text-gray-900 {
                            color: #111827 !important;
                        }
                        a {
                            color: ${primaryColor} !important;
                            text-decoration: none !important;
                        }
                        button, [style*="padding-top: 120px"] {
                            display: none !important;
                        }
                    }
                `}</style>

                {downloadState.isOpen && invoice && (
                    <InvoiceDownload
                        invoice={invoice}
                        order={order}
                        user={receiptUser}
                        documentRef={receiptContainerRef}
                        documentType={documentType}
                        onClose={handleCloseDownload}
                        isLoading={downloadState.isLoading}
                        error={downloadState.error}
                    />
                )}
            </div>
        </div>
    );
}

export default ViewInvoice;
